/**
 * 노드 이름 목록 → Building/Floor 하이라키 빌더 (three 비의존).
 *
 * admin은 업로드한 GLB의 JSON 청크에서 노드 이름만 추출해 이 함수로 트리를 만든다
 * (geometry 디코딩 불필요). app은 GLB를 직접 로드하므로 별도 three 기반 인덱서를 쓴다.
 */
import { parseMeshName, floorLabel, floorOrder, type FloorKey } from "./naming";

/** 모델에서 추출한 순수 구조 (날짜 없음) */
export interface HierarchyFloor {
  code: FloorKey;
  label: string;
  order: number;
}
export interface HierarchyBuilding {
  code: string;
  floors: HierarchyFloor[];
}
export interface ModelHierarchy {
  buildings: HierarchyBuilding[];
}

/** GLB 노드 이름들 → Building/Floor 트리 (정렬·중복제거 포함) */
export function buildHierarchyFromNames(names: Iterable<string>): ModelHierarchy {
  const byBuilding = new Map<string, Set<FloorKey>>();

  for (const name of names) {
    const info = parseMeshName(name);
    if (!info.buildingId) continue;
    let floors = byBuilding.get(info.buildingId);
    if (!floors) {
      floors = new Set<FloorKey>();
      byBuilding.set(info.buildingId, floors);
    }
    if (info.floor) floors.add(info.floor);
  }

  const buildings: HierarchyBuilding[] = [...byBuilding.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([code, floorSet]) => ({
      code,
      floors: [...floorSet]
        .map((f) => ({ code: f, label: floorLabel(f), order: floorOrder(f) }))
        .sort((a, b) => a.order - b.order),
    }));

  return { buildings };
}

// ---------------------------------------------------------------------------
// modelHierarchy 저장 문서 (구조 + 층별 일정). site.modelHierarchy(string)에 직렬화.
// ---------------------------------------------------------------------------

export const MODEL_HIERARCHY_SPEC_VERSION = 1;

export interface FloorSchedule extends HierarchyFloor {
  /** 목표 완료일 (YYYY-MM-DD). 미입력 시 null */
  plannedDate?: string | null;
  /** 실제 완료일 (YYYY-MM-DD). 미입력 시 null */
  actualDate?: string | null;
}
export interface BuildingSchedule {
  code: string;
  /** admin 편집 가능 표시명 (예: "101동") */
  name?: string;
  floors: FloorSchedule[];
}
export interface ModelHierarchyDoc {
  specVersion: number;
  /** staleness 감지 — 현 site.modelFile.id 와 다르면 재분석 필요 */
  sourceModelFileId?: number | null;
  /** 생성/갱신 일시 (호출부에서 주입 — 패키지는 시간 의존 X) */
  generatedAt?: string;
  buildings: BuildingSchedule[];
}

/** site.modelHierarchy(string|null) → 문서 객체. 실패 시 null. */
export function parseModelHierarchy(json: string | null | undefined): ModelHierarchyDoc | null {
  if (!json) return null;
  try {
    const doc = JSON.parse(json) as ModelHierarchyDoc;
    if (!doc || !Array.isArray(doc.buildings)) return null;
    return doc;
  } catch {
    return null;
  }
}

/** 문서 객체 → site.modelHierarchy 저장용 문자열 */
export function serializeModelHierarchy(doc: ModelHierarchyDoc): string {
  return JSON.stringify(doc);
}

/**
 * 모델에서 새로 추출한 구조 + 기존 일정 = 병합된 BuildingSchedule[].
 *
 * #63 핵심 규칙: 모델 재업로드 시 구조는 재생성하되, **기존 날짜는
 * (buildingCode, floorCode) 키로 매칭해 보존**한다. 모델에서 사라진 층은 드롭,
 * 새로 생긴 층은 날짜 null로 추가.
 */
export function mergeSchedule(
  structure: ModelHierarchy,
  previous: ModelHierarchyDoc | null
): BuildingSchedule[] {
  const prevBuildings = new Map<string, BuildingSchedule>();
  for (const b of previous?.buildings ?? []) prevBuildings.set(b.code, b);

  return structure.buildings.map((building) => {
    const prevBuilding = prevBuildings.get(building.code);
    const prevFloors = new Map<string, FloorSchedule>();
    for (const f of prevBuilding?.floors ?? []) prevFloors.set(f.code, f);

    return {
      code: building.code,
      name: prevBuilding?.name,
      floors: building.floors.map((floor) => {
        const prevFloor = prevFloors.get(floor.code);
        return {
          code: floor.code,
          label: floor.label,
          order: floor.order,
          plannedDate: prevFloor?.plannedDate ?? null,
          actualDate: prevFloor?.actualDate ?? null,
        };
      }),
    };
  });
}
